import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import CreatorHeader from '../../components/creator/CreatorHeader';

let field_cls = 'w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 '
  + 'rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

export default function CreatorProfile() {
  const navigate = useNavigate();
  const [search_params] = useSearchParams();
  const is_dbg = search_params.get('dbg') == '1';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connections, set_connections] = useState<any[]>([]);
  const [connecting, set_connecting] = useState<string | null>(null);
  const [debug_posts, set_debug_posts] = useState<Record<string, any[]>>({});
  const [debug_stats, set_debug_stats] = useState<Record<string, {followers: number, engagement: number}>>({});
  const [debug_loading, set_debug_loading] = useState(false);
  const [alert_msg, set_alert_msg] = useState<string>('');

  const [formData, setFormData] = useState({
    username: '',
    first: '',
    last: '',
    social_links: {
      youtube: '',
      instagram: '',
      tiktok: '',
      twitter: ''
    },
  });


  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (!is_dbg || !connections.length)
      return;
    let active = connections.filter(c => c.is_active);
    if (!active.length)
      return;
    set_debug_loading(true);
    Promise.all(active.map(async c => {
      let res = await back_app.social_recent_posts(c.id);
      return {platform: c.platform, posts: res.posts || [],
        followers: res.followers || 0, engagement: res.engagement || 0};
    })).then(results => {
      let by_platform: Record<string, any[]> = {};
      let stats: Record<string, {followers: number, engagement: number}> = {};
      for (let r of results)
      {
        by_platform[r.platform] = r.posts;
        stats[r.platform] = {followers: r.followers,
          engagement: r.engagement};
      }
      set_debug_posts(by_platform);
      set_debug_stats(stats);
    }).catch(e => console.error('debug posts error:', e))
      .finally(() => set_debug_loading(false));
  }, [is_dbg, connections]);

  const fetchProfile = async () => {
    try {
      const result = await back_app.cprofile_get();
      if (result?.err)
        throw new Error(result.err);
      setFormData(prev => ({
        ...prev,
        username: result.username || '',
        first: result.first || '',
        last: result.last || '',
        social_links: {...prev.social_links, ...result.social_links},
      }));
      const conn_res = await back_app.social_connections();
      if (!conn_res.err && Array.isArray(conn_res))
        set_connections(conn_res);
    } catch (error) {
      console.error('Error fetching profile:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };


  const handle_connect = async (platform: 'facebook' | 'instagram') => {
    try {
      set_connecting(platform);
      const result = await back_app.social_auth_url(platform);
      if (!result.err && result.auth_url)
        window.location.href = result.auth_url;
      else
      {
        alert('Failed to initiate connection');
        set_connecting(null);
      }
    } catch (error) {
      console.error('Connection error:', error);
      alert('Failed to connect');
      set_connecting(null);
    }
  };

  const is_connected = (platform: string) =>
    connections.some(c => c.platform == platform && c.is_active);

  const get_connection = (platform: string) =>
    connections.find(c => c.platform == platform && c.is_active);

  const handleSave = async () => {
    if (!formData.username.trim()) {
      set_alert_msg('Please enter a username');
      return;
    }
    setSaving(true);
    try {
      const result = await back_app.cprofile_set(formData);
      if (result.err)
        return set_alert_msg(`Error: ${result.err}`);
      set_alert_msg('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      set_alert_msg('Error saving profile');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">My Profile</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your personal information</p>
          </div>
          <button
            onClick={() => navigate('/creator/org')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            Channel Profile →
          </button>
        </div>

        {}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 sm:p-6 border-b">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Basic Information</h3>
          </div>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Username <span className="text-red-500">*</span>
              </label>
              <input type="text" value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={field_cls} placeholder="Your username" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                <input type="text" value={formData.first}
                  onChange={(e) => setFormData({ ...formData, first: e.target.value })}
                  className={field_cls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                <input type="text" value={formData.last}
                  onChange={(e) => setFormData({ ...formData, last: e.target.value })}
                  className={field_cls} />
              </div>
            </div>
          </div>
        </div>


        {}
        {is_dbg && (
        <div className="bg-white rounded-xl border shadow-sm mt-4 sm:mt-6">
          <div className="p-4 sm:p-6 border-b">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Social Media</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Connect your social media accounts</p>
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎥 YouTube
              </label>
              <input
                type="url"
                value={formData.social_links.youtube}
                onChange={(e) => setFormData({
                  ...formData,
                  social_links: { ...formData.social_links, youtube: e.target.value }
                })}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://youtube.com/@username"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📸 Instagram
              </label>
              <input
                type="url"
                value={formData.social_links.instagram}
                onChange={(e) => setFormData({
                  ...formData,
                  social_links: { ...formData.social_links, instagram: e.target.value }
                })}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://instagram.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎵 TikTok
              </label>
              <input
                type="url"
                value={formData.social_links.tiktok}
                onChange={(e) => setFormData({
                  ...formData,
                  social_links: { ...formData.social_links, tiktok: e.target.value }
                })}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://tiktok.com/@username"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🐦 Twitter/X
              </label>
              <input
                type="url"
                value={formData.social_links.twitter}
                onChange={(e) => setFormData({
                  ...formData,
                  social_links: { ...formData.social_links, twitter: e.target.value }
                })}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://twitter.com/username"
              />
            </div>
          </div>
        </div>
        )}

        {}
        {is_dbg && (
        <div className="bg-white rounded-xl border shadow-sm mt-4 sm:mt-6">
          <div className="p-4 sm:p-6 border-b">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              Social Integrations
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Connect accounts to publish videos directly
            </p>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white text-lg">
                  f
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Facebook</div>
                  {is_connected('facebook') ? (
                    <div className="text-xs text-green-600">
                      Connected as {get_connection('facebook')?.platform_username}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">Not connected</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => handle_connect('facebook')}
                disabled={connecting == 'facebook'}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                  is_connected('facebook')
                    ? 'border border-blue-500 text-blue-600 hover:bg-blue-50'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } ${connecting == 'facebook' ? 'opacity-50' : ''}`}
              >
                {connecting == 'facebook' ? 'Connecting...'
                  : is_connected('facebook') ? 'Reconnect' : 'Connect'}
              </button>
            </div>
            {}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-lg">
                  ig
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Instagram</div>
                  {is_connected('instagram') ? (
                    <div className="text-xs text-green-600">
                      Connected as @{get_connection('instagram')?.platform_username}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">Not connected</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => handle_connect('instagram')}
                disabled={connecting == 'instagram'}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                  is_connected('instagram')
                    ? 'border border-pink-500 text-pink-600 hover:bg-pink-50'
                    : 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white hover:shadow-lg'
                } ${connecting == 'instagram' ? 'opacity-50' : ''}`}
              >
                {connecting == 'instagram' ? 'Connecting...'
                  : is_connected('instagram') ? 'Reconnect' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
        )}

        {}
        {is_dbg && (
          <div className="bg-amber-50 rounded-xl border-2 border-amber-400 shadow-sm mt-4 sm:mt-6">
            <div className="p-4 sm:p-6 border-b border-amber-300">
              <h3 className="text-base sm:text-lg font-bold text-amber-900">
                [DEBUG] Recent Posts & Analytics
              </h3>
            </div>
            <div className="p-4 sm:p-6">
              {debug_loading && (
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  Loading posts...
                </div>
              )}
              {!debug_loading && !Object.keys(debug_posts).length && (
                <p className="text-sm text-amber-700">
                  No connected accounts or no posts found
                </p>
              )}
              {Object.entries(debug_posts).map(([platform, posts]) => (
                <div key={platform} className="mb-6 last:mb-0">
                  <h4 className="font-bold text-amber-900 text-sm mb-2 uppercase">
                    {platform}
                  </h4>
                  {debug_stats[platform] && (
                    <div className="flex gap-4 mb-3 text-xs">
                      <span className="px-2 py-1 bg-amber-100 rounded">
                        Followers: <b>{debug_stats[platform].followers}</b>
                      </span>
                      <span className="px-2 py-1 bg-amber-100 rounded">
                        Engagement (last 10): <b>{debug_stats[platform].engagement}</b>
                      </span>
                    </div>
                  )}
                  {!posts.length && (
                    <p className="text-xs text-amber-700">No posts found</p>
                  )}
                  <div className="space-y-4">
                    {posts.map((post: any) => (
                      <div key={post.id} className="bg-white rounded-lg border p-3 flex gap-3">
                        {post.image && (
                          <img src={post.image} alt="" className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 mb-1">
                            {new Date(post.created_time).toLocaleDateString()}
                            {post.media_type && <span className="ml-2 text-amber-600">{post.media_type}</span>}
                          </p>
                          <p className="text-sm text-gray-900 truncate mb-2">
                            {post.message || '(no caption)'}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                            <span>Likes: <b>{post.likes || 0}</b></span>
                            <span>Comments: <b>{post.comments || 0}</b></span>
                            {post.shares != undefined && (
                              <span>Shares: <b>{post.shares}</b></span>
                            )}
                            {post.views != undefined && post.views > 0 && (
                              <span>Views: <b>{post.views}</b></span>
                            )}
                            {post.impressions != undefined && (
                              <span>Impressions: <b>{post.impressions}</b></span>
                            )}
                            {post.reach != undefined && (
                              <span>Reach: <b>{post.reach}</b></span>
                            )}
                            {post.engaged_users != undefined && (
                              <span>Engaged: <b>{post.engaged_users}</b></span>
                            )}
                            {post.clicks != undefined && (
                              <span>Clicks: <b>{post.clicks}</b></span>
                            )}
                            {post.saved != undefined && (
                              <span>Saved: <b>{post.saved}</b></span>
                            )}
                            {post.plays != undefined && (
                              <span>Plays: <b>{post.plays}</b></span>
                            )}
                          </div>
                          {post.url && (
                            <a href={post.url} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline mt-1 inline-block">
                              View post
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:flex-1 px-4 sm:px-6 py-3 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-lg font-semibold text-sm sm:text-base hover:shadow-lg transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="w-full sm:w-auto px-4 sm:px-6 py-3 border border-gray-300 rounded-lg font-semibold text-sm sm:text-base text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    {alert_msg && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={() => set_alert_msg('')}>
        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
          onClick={e => e.stopPropagation()}>
          <p className="text-gray-900 text-sm sm:text-base mb-5">{alert_msg}</p>
          <div className="flex justify-end">
            <button
              onClick={() => set_alert_msg('')}
              className="px-4 py-2 bg-gradient-brand text-white rounded-lg
                font-semibold text-sm hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}